import './Assistant.css';

import {
  Box,
  Button,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import AdvancedInstructions from 'pages/ScribeDashboard/MiddlePanel/components/AdvancedInstructions';
import React, { useEffect } from 'react';
import * as Yup from 'yup';

// @ts-ignore
import RichTextEditor from '../ScribeDashboard/MiddlePanel/components/RichTextEditor';

interface Assistant {
  id: string;
  name: string;
  category: string;
  language: string;
  share_group: false;
  share_global: false;
  advanced_instructions: string;
  status: string;
  example1: string;
  example2: string;
}

const formSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  category: Yup.string().required('Category is required'),
  language: Yup.string().required('Language is required'),
  example1: Yup.string().required('Document 1 is required'),
  example2: Yup.string().required('Document 2 is required'),
  advanceInstructions: Yup.string().required(
    'Advance Instructions is required'
  ),
});
const AssistantAddForm = ({
  closeForm,
  selectedAssistant,
  updateGlobalData,
  newCategory,
}: {
  closeForm: () => void;
  selectedAssistant: Assistant | null;
  updateGlobalData: (newGlobalData: any) => void;
  newCategory: null;
}) => {
  const user = JSON.parse(localStorage.getItem('roles') || '{}');
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const theme = useTheme();
  const underSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile375 = useMediaQuery('(max-width:375px)');

  const oauthToken = localStorage.getItem('AUTH_TOKEN') || '';

  if (!oauthToken) {
    throw new Error('OAuth token is missing');
  }

  const fetchInstructions = async () => {
    try {
      const response = await fetch(
        `${baseURL}/api/instructions?userId=${user?.id}&category=${newCategory}&status=Active`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oauthToken}`,
          },
        }
      );
      const data = await response.json();
      updateGlobalData(data);
    } catch (error) {
      console.error('Error fetching instructions:', error);
    }
  };

  const fetchAssistants = async () => {
    try {
      const response = await fetch(
        `${baseURL}/api/instructions?userId=${user.id}&status=Active`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oauthToken}`,
          },
        }
      );
      const data = await response.json();
      updateGlobalData(data);
    } catch (error) {
      console.error('Error fetching assistants:', error);
      alert('Failed to load assistants');
    }
  };

  // UseEffect that chooses the correct function
  useEffect(() => {
    if (newCategory) {
      fetchInstructions();
    } else {
      fetchAssistants();
    }
  }, [newCategory]);

  const languageOptions = [
    { value: 'english', display: 'English' },
    { value: 'german', display: 'German' },
    { value: 'russian', display: 'Russian' },
    { value: 'italian', display: 'Italian' },
    { value: 'polish', display: 'Polish' },
    { value: 'ukrainian', display: 'Ukrainian' },
    { value: 'french', display: 'French' },
    { value: 'spanish', display: 'Spanish' },
  ];

  interface Examples {
    example1: string;
    example2: string;
  }

  const generateInstructionString = (
    category: string,
    language: string,
    examples: Examples,
    advancedInstructions = ''
  ) => {
    if (!category) return '';

    // Base instruction that's common for all types
    let baseString = 'You are a medical transcription assistant. ';

    // Add category-specific instructions
    switch (category.toLowerCase()) {
      case 'report':
        baseString +=
          'you will generate a medical report, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'procedure':
        baseString +=
          'you will generate a medical procedure note, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'letter':
        baseString +=
          'you will generate a medical letter, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary unless otherwise specified. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      case 'custom':
        baseString +=
          'you will generate a medical report, using the first sample provided below as a normal template. ' +
          'You will use formal, technical vocabulary. You will be presented with a transcript, and you will ' +
          'generate your report substituting any abnormal findings and the overall impression into the format ' +
          'shown in the attached examples.';
        break;
      default:
        console.warn(`Unexpected category: ${category}`);
        break;
    }

    // Add language specification
    const selectedLanguage = languageOptions.find(
      (lang) => lang.value === language
    )?.display;
    if (!selectedLanguage) {
      console.warn(
        `Language not found for value: ${language}, defaulting to English`
      );
    }
    baseString += `\n\n### Output language must be ${
      selectedLanguage || 'English'
    }`;

    // Add advanced instructions if present
    if (advancedInstructions && advancedInstructions.trim()) {
      baseString += `\n\n### Advanced Instructions:\n${advancedInstructions.trim()}`;
    }

    // Add standard instruction about ignoring casual dialogue
    baseString +=
      '\n\n###ignore casual or non-medical dialogue. Focus on organizing the relevant information into a medical note. ' +
      'If some parts of the input are unclear, process as much as possible based on the available information. ' +
      'Process the text as-is, treating interruptions or incomplete sentences as part of the conversation. ' +
      'Extract meaningful content for a medical note.';

    // Add examples - handle both object and direct example1/example2 format
    if (examples.example1 || examples.example2) {
      if (examples.example1?.trim()) {
        baseString += `\n\nExample 1:\n${examples.example1.trim()}`;
      }
      if (examples.example2?.trim()) {
        baseString += `\n\nExample 2:\n${examples.example2.trim()}`;
      }
    } else if (typeof examples === 'object') {
      // If examples is passed directly as { example1, example2 }
      baseString += `\n\nExample 1:\n${(examples.example1 || '').trim()}`;
      baseString += `\n\nExample 2:\n${(examples.example2 || '').trim()}`;
    }

    return baseString;
  };

  const handleApiCall = async (values: any, { resetForm }: any) => {
    if (selectedAssistant) {
      // Editing an existing assistant
      if (
        !values.name ||
        !values.category ||
        !values.language ||
        !values.advanceInstructions ||
        !(values.example1 && values.example1.trim()) ||
        !(values.example2 && values.example2.trim())
      ) {
        alert(
          'Please fill in all required fields (Name, Category, Language, and Examples)'
        );
        return;
      }

      try {
        const instructionString = generateInstructionString(
          values.category,
          values.language,
          {
            example1: values.example1,
            example2: values.example2,
          },
          values.advanceInstructions
        );
        const response = await fetch(
          `${baseURL}/api/assistant-instructions/${selectedAssistant.id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
            },
            body: JSON.stringify({
              name: values.name,
              category: values.category,
              advanced_instructions: values.advanceInstructions,
              status: 'Active',
              language: values.language,
              example1: values.example1,
              example2: values.example2,
              instruction: instructionString,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error response:', errorData);
          throw new Error(errorData.message || 'Failed to update assistant');
        }

        alert('Assistant updated successfully');
        if (newCategory) {
          fetchInstructions();
        } else {
          fetchAssistants();
        }
        closeForm();
      } catch (error: any) {
        console.error('Error updating assistant:', error);
        alert(`Failed to update assistant: ${error.message}`);
      }
    } else {
      // Creating a new assistant
      if (
        !values.name ||
        !values.category ||
        !values.language ||
        !values.advanceInstructions ||
        !(values.example1 && values.example1.trim()) ||
        !(values.example2 && values.example2.trim())
      ) {
        alert(
          'Please fill in all required fields (Name, Category, Language, and Documents)'
        );
        return;
      }

      try {
        const instructionString = generateInstructionString(
          values.category,
          values.language,
          {
            example1: values.example1,
            example2: values.example2,
          },
          values.advanceInstructions
        );
        const response = await fetch(`${baseURL}/api/assistant-instructions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
          },
          body: JSON.stringify({
            userid: user.id,
            account_id: user.account_id,
            name: values.name,
            category: values.category,
            advanced_instructions: values.advanceInstructions,
            status: 'Active',
            language: values.language,
            example1: values.example1,
            example2: values.example2,
            instruction: instructionString,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create assistant');
        }

        alert('Assistant created successfully');
        resetForm(); // Reset the form after submission
        if (newCategory) {
          fetchInstructions();
        } else {
          fetchAssistants();
        }
        closeForm();
      } catch (error: any) {
        console.error('Error creating assistant:', error);
        alert(`Failed to create assistant: ${error.message}`);
      }
    }
  };

  let formWidthClass = 'w-[775px]'; // default
  if (isMobile375) {
    formWidthClass = 'w-[363px]';
  } else if (underSm) {
    formWidthClass = 'w-[402px]';
  }

  return (
    <Formik
      initialValues={{
        name: selectedAssistant ? selectedAssistant?.name : '',
        category: selectedAssistant
          ? selectedAssistant?.category
          : 'Select Category',
        language: selectedAssistant
          ? selectedAssistant?.language
          : 'Select Language',
        advanceInstructions: selectedAssistant
          ? selectedAssistant?.advanced_instructions
          : '',
        example1: selectedAssistant ? selectedAssistant?.example1 : '',
        example2: selectedAssistant ? selectedAssistant?.example2 : '',
      }}
      validationSchema={formSchema}
      onSubmit={handleApiCall}
    >
      {({ errors, touched, values, setFieldValue }: any) => (
        <Form
          className={`mx-auto mt-2 ${formWidthClass} space-y-6 rounded-md p-3 px-0 pl-1`}
        >
          <div className="flex h-[calc(100vh-170px)] flex-col gap-3 overflow-auto pr-2">
            {/* Name Input */}
            <Box>
              <FormLabel htmlFor="name" sx={{ fontSize: '0.9rem' }}>
                Name
              </FormLabel>
              <Field
                id="name"
                name="name"
                as={TextField}
                fullWidth
                variant="outlined"
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                InputProps={{ sx: { fontSize: '0.9rem' } }}
                placeholder="Enter a name for this instruction set"
              />
            </Box>

            {/* Category Dropdown */}
            <Box>
              <FormLabel htmlFor="category" sx={{ fontSize: '0.9rem' }}>
                Category
              </FormLabel>
              <Field
                as={Select}
                id="category"
                name="category"
                fullWidth
                variant="outlined"
                error={touched.category && !!errors.category}
                displayEmpty
                value={values.category}
                onChange={(e: any) => setFieldValue('category', e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Category
                </MenuItem>
                <MenuItem value="report">Report</MenuItem>
                <MenuItem value="procedure">Procedure</MenuItem>
                <MenuItem value="letter">Letter</MenuItem>
                <MenuItem value="Custom">Custom Note</MenuItem>
              </Field>
              {touched.category && errors.category && (
                <div style={{ color: 'red', fontSize: '0.75rem' }}>
                  {errors.category}
                </div>
              )}
            </Box>

            {/* Language Dropdown */}
            <Box>
              <FormLabel htmlFor="language" sx={{ fontSize: '0.9rem' }}>
                Language
              </FormLabel>
              <Field
                as={Select}
                id="language"
                name="language"
                fullWidth
                variant="outlined"
                error={touched.language && !!errors.language}
                displayEmpty
                value={values.language}
                onChange={(e: any) => setFieldValue('language', e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Language
                </MenuItem>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="german">German</MenuItem>
                <MenuItem value="russian">Russian</MenuItem>
                <MenuItem value="italian">Italian</MenuItem>
                <MenuItem value="polish">Polish</MenuItem>
                <MenuItem value="ukrainian">Ukrainian</MenuItem>
                <MenuItem value="french">French</MenuItem>
                <MenuItem value="spanish">Spanish</MenuItem>
              </Field>
              {touched.language && errors.language && (
                <div style={{ color: 'red', fontSize: '0.75rem' }}>
                  {errors.language}
                </div>
              )}
            </Box>

            {/* Advance Instructions */}
            <Box>
              <FormLabel
                htmlFor="advanceInstructions"
                sx={{ fontSize: '0.9rem' }}
              >
                Advanced Instructions
              </FormLabel>
              <AdvancedInstructions
                value={
                  selectedAssistant
                    ? selectedAssistant.advanced_instructions
                    : ''
                }
                onChange={(data: { instructionsText: string; json: any }) => {
                  setFieldValue('advanceInstructions', data.instructionsText);
                }}
              />
            </Box>

            {/* Document 1 */}
            <Box>
              <FormLabel htmlFor="example1" sx={{ fontSize: '0.9rem' }}>
                Sample Document 1
              </FormLabel>
              <RichTextEditor
                value={values.example1}
                onChange={(value: any) => setFieldValue('example1', value)}
                setActiveTextArea={() => {}}
              />
              {touched.example1 && errors.example1 && (
                <div
                  style={{
                    color: '#F04438',
                    fontSize: '0.75rem',
                    marginLeft: '14px',
                  }}
                >
                  {errors.example1}
                </div>
              )}
            </Box>

            {/* Document 2 */}
            <Box>
              <FormLabel htmlFor="example2" sx={{ fontSize: '0.9rem' }}>
                Sample Document 2
              </FormLabel>
              <RichTextEditor
                value={values.example2}
                onChange={(value: any) => setFieldValue('example2', value)}
                setActiveTextArea={() => {}}
              />
              {touched.example2 && errors.example2 && (
                <div
                  style={{
                    color: '#F04438',
                    fontSize: '0.75rem',
                    marginLeft: '14px',
                  }}
                >
                  {errors.example2}
                </div>
              )}
            </Box>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-between pr-2">
            <Button
              variant="outlined"
              type="button"
              color="secondary"
              className="w-[48%] py-2"
              onClick={closeForm}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="w-[48%] bg-[#11AC9E] py-2 hover:bg-[#90BEE6]"
            >
              {selectedAssistant ? 'Save' : 'Create Assistant'}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AssistantAddForm;
